import React, { useState, useEffect } from 'react';
import { Building2, Users, DollarSign, Settings, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currency';

interface Gym {
  gymId: string; // GUID as string
  gymName: string;
  ownerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  subscriptionPlan: string;
  isActive: boolean;
  isTrial: boolean;
  trialDaysRemaining: number;
  currentMemberCount: number;
  currentStaffCount: number;
  maxMembers: number;
  maxStaff: number;
  monthlyPrice: number;
  totalRevenue: number;
  createdAt: string;
  logoUrl?: string;
}

interface PlatformStats {
  totalGyms: number;
  activeGyms: number;
  trialGyms: number;
  paidGyms: number;
  totalRevenue: number;
  monthlyRecurring: number;
  byPlan: { plan: string; count: number }[];
  totalMembers: number;
  totalStaff: number;
}

const SuperAdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [updating, setUpdating] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds to keep data in sync
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds

    // Refresh when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty dependency array - run only once on mount

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gymsData, statsData] = await Promise.all([
        api.get('/Gym'),
        api.get('/Gym/Stats')
      ]);
      setGyms(gymsData.data);
      setStats(statsData.data);
    } catch (err: any) {
      error('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageGym = (gym: Gym) => {
    setSelectedGym(gym);
    setLogoPreview(gym.logoUrl || '');
    setLogoFile(null);
    setShowManageModal(true);
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        error('Image size should be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        error('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setLogoPreview(base64String);
        setLogoFile(file);
        if (selectedGym) {
          setSelectedGym({ ...selectedGym, logoUrl: base64String });
        }
      };
      reader.readAsDataURL(file);
    }
    
    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleUpdateGym = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGym) return;
    
    setUpdating(true);
    try {
      await api.put(`/Gym/${selectedGym.gymId}`, {
        gymName: selectedGym.gymName,
        ownerName: selectedGym.ownerName,
        phone: selectedGym.phone,
        address: selectedGym.address,
        city: selectedGym.city,
        state: selectedGym.state,
        subscriptionPlan: selectedGym.subscriptionPlan,
        isActive: selectedGym.isActive,
        logoUrl: selectedGym.logoUrl
      });
      success('Gym updated successfully!');
      setShowManageModal(false);
      setSelectedGym(null);
      fetchData(); // Refresh data
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update gym');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'Trial': return { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' };
      case 'Basic': return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
      case 'Pro': return { bg: '#D1FAE5', text: '#065F46', border: '#10B981' };
      case 'Enterprise': return { bg: '#F3E8FF', text: '#6B21A8', border: '#8B5CF6' };
      default: return { bg: '#F3F4F6', text: '#374151', border: '#9CA3AF' };
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
          🏋️ Platform Overview
        </h1>
        <p style={{ color: '#6B7280' }}>Monitor all gyms and track platform performance</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>Total Gyms</p>
                <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#3B82F6', marginTop: '0.5rem' }}>
                  {stats.totalGyms}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#10B981', marginTop: '0.25rem' }}>
                  {stats.activeGyms} active
                </p>
              </div>
              <Building2 size={48} style={{ color: '#3B82F6', opacity: 0.2 }} />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>Total Members</p>
                <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#8B5CF6', marginTop: '0.5rem' }}>
                  {stats.totalMembers}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                  Across all gyms
                </p>
              </div>
              <Users size={48} style={{ color: '#8B5CF6', opacity: 0.2 }} />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>Total Staff</p>
                <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#EC4899', marginTop: '0.5rem' }}>
                  {stats.totalStaff}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                  Across all gyms
                </p>
              </div>
              <Users size={48} style={{ color: '#EC4899', opacity: 0.2 }} />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>MRR</p>
                <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#10B981', marginTop: '0.5rem' }}>
                  {formatCurrency(stats.monthlyRecurring, true)}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                  {stats.paidGyms} paying gyms
                </p>
              </div>
              <DollarSign size={48} style={{ color: '#10B981', opacity: 0.2 }} />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>Trial Gyms</p>
                <h3 style={{ fontSize: '2rem', fontWeight: '700', color: '#F59E0B', marginTop: '0.5rem' }}>
                  {stats.trialGyms}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                  To convert
                </p>
              </div>
              <Clock size={48} style={{ color: '#F59E0B', opacity: 0.2 }} />
            </div>
          </div>
        </div>
      )}

      {/* Plan Distribution */}
      {stats && stats.byPlan.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>Subscription Distribution</h3>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {stats.byPlan.map(({ plan, count }) => {
              const colors = getPlanColor(plan);
              const percentage = stats.totalGyms > 0 ? (count / stats.totalGyms * 100) : 0;
              return (
                <div
                  key={plan}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    borderRadius: '8px',
                    backgroundColor: colors.bg,
                    border: `2px solid ${colors.border}`,
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '0.875rem', color: colors.text, fontWeight: '600', marginBottom: '0.5rem' }}>
                    {plan}
                  </div>
                  <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.text }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: colors.text, opacity: 0.8 }}>
                    {percentage.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gyms List */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>All Gyms ({gyms.length})</h3>
        
        <div style={{ display: 'grid', gap: '1rem' }}>
          {gyms.map((gym) => {
            const planColors = getPlanColor(gym.subscriptionPlan);
            const memberUsage = gym.maxMembers > 0 ? (gym.currentMemberCount / gym.maxMembers * 100) : 0;
            
            return (
              <div
                key={gym.gymId}
                style={{
                  padding: '1.5rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '1rem',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3B82F6';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>
                      {gym.gymName}
                    </h4>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: planColors.bg,
                      color: planColors.text,
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {gym.subscriptionPlan}
                    </span>
                    {gym.isActive ? (
                      <CheckCircle size={20} style={{ color: '#10B981' }} />
                    ) : (
                      <XCircle size={20} style={{ color: '#EF4444' }} />
                    )}
                    {gym.isTrial && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        backgroundColor: '#FEF3C7',
                        color: '#92400E',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        <AlertCircle size={14} />
                        {gym.trialDaysRemaining} days left
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                    <div>👤 <strong>Owner:</strong> {gym.ownerName}</div>
                    <div>📧 {gym.email}</div>
                    <div>📞 {gym.phone}</div>
                    <div>📍 {gym.city}, {gym.state}</div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                    <div>
                      <span style={{ color: '#6B7280' }}>Members: </span>
                      <strong style={{ color: memberUsage > 90 ? '#EF4444' : '#111827' }}>
                        {gym.currentMemberCount}/{gym.maxMembers}
                      </strong>
                      {memberUsage > 90 && (
                        <span style={{ color: '#EF4444', fontSize: '0.75rem', marginLeft: '0.25rem' }}>
                          ({memberUsage.toFixed(0)}% used)
                        </span>
                      )}
                    </div>
                    <div>
                      <span style={{ color: '#6B7280' }}>Staff: </span>
                      <strong>{gym.currentStaffCount}/{gym.maxStaff}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280' }}>Total Revenue: </span>
                      <strong style={{ color: '#10B981' }}>{formatCurrency(gym.totalRevenue)}</strong>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10B981' }}>
                    {formatCurrency(gym.monthlyPrice)}/mo
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Since {new Date(gym.createdAt).toLocaleDateString()}
                  </div>
                  <button
                    onClick={() => handleManageGym(gym)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#F3F4F6',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: 'auto'
                    }}
                  >
                    <Settings size={16} />
                    Manage
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Manage Gym Modal */}
      {showManageModal && selectedGym && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '2rem',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
                Manage Gym: {selectedGym.gymName}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {selectedGym.isActive ? (
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#D1FAE5',
                    color: '#065F46',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    Active
                  </span>
                ) : (
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#FEE2E2',
                    color: '#991B1B',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    Inactive
                  </span>
                )}
              </div>
            </div>

            <form onSubmit={handleUpdateGym}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Gym Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={selectedGym.gymName}
                      onChange={(e) => setSelectedGym({ ...selectedGym, gymName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Owner Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={selectedGym.ownerName}
                      onChange={(e) => setSelectedGym({ ...selectedGym, ownerName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Email (Read-only)
                    </label>
                    <input
                      type="email"
                      disabled
                      value={selectedGym.email}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: '#F9FAFB',
                        color: '#6B7280'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={selectedGym.phone}
                      onChange={(e) => setSelectedGym({ ...selectedGym, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={selectedGym.address}
                    onChange={(e) => setSelectedGym({ ...selectedGym, address: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      City *
                    </label>
                    <input
                      type="text"
                      required
                      value={selectedGym.city}
                      onChange={(e) => setSelectedGym({ ...selectedGym, city: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      State *
                    </label>
                    <input
                      type="text"
                      required
                      value={selectedGym.state}
                      onChange={(e) => setSelectedGym({ ...selectedGym, state: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Subscription Plan *
                    </label>
                    <select
                      value={selectedGym.subscriptionPlan}
                      onChange={(e) => setSelectedGym({ ...selectedGym, subscriptionPlan: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="Trial">Trial (Free)</option>
                      <option value="Basic">Basic (₹3,999/month)</option>
                      <option value="Pro">Pro (₹7,999/month)</option>
                      <option value="Enterprise">Enterprise (₹14,999/month)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      Status
                    </label>
                    <select
                      value={selectedGym.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setSelectedGym({ ...selectedGym, isActive: e.target.value === 'active' })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                    Gym Logo (Optional)
                  </label>
                  
                  {/* File Upload Option */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ 
                      display: 'inline-block', 
                      padding: '0.75rem 1.5rem', 
                      backgroundColor: '#4F46E5', 
                      color: 'white', 
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                        style={{ display: 'none' }}
                      />
                      📁 Upload from Device
                    </label>
                    <span style={{ marginLeft: '1rem', color: '#6B7280', fontSize: '0.875rem' }}>or</span>
                  </div>

                  {/* URL Input Option */}
                  <input
                    type="url"
                    value={selectedGym.logoUrl && !selectedGym.logoUrl.startsWith('data:') ? selectedGym.logoUrl : ''}
                    onChange={(e) => {
                      setSelectedGym({ ...selectedGym, logoUrl: e.target.value });
                      setLogoPreview(e.target.value);
                      setLogoFile(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    placeholder="Enter logo image URL (e.g., https://example.com/logo.png)"
                  />
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                    Upload a logo from your device or enter a direct URL. This will appear on payment receipts.
                  </small>
                  
                  {/* Logo Preview */}
                  {(logoPreview || (selectedGym.logoUrl && selectedGym.logoUrl.startsWith('data:'))) && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <img 
                        src={logoPreview || selectedGym.logoUrl} 
                        alt="Logo Preview" 
                        style={{ maxWidth: '150px', maxHeight: '80px', border: '1px solid #D1D5DB', borderRadius: '8px', padding: '0.5rem', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      {logoFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogoPreview(selectedGym?.logoUrl || '');
                            setLogoFile(null);
                            if (selectedGym) {
                              setSelectedGym({ ...selectedGym, logoUrl: selectedGym.logoUrl || '' });
                            }
                          }}
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Gym Stats (Read-only) */}
                <div style={{
                  backgroundColor: '#F9FAFB',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginTop: '1rem'
                }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>Gym Statistics</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
                    <div>
                      <span style={{ color: '#6B7280' }}>Members: </span>
                      <strong>{selectedGym.currentMemberCount} / {selectedGym.maxMembers}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280' }}>Staff: </span>
                      <strong>{selectedGym.currentStaffCount} / {selectedGym.maxStaff}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280' }}>Total Revenue: </span>
                      <strong style={{ color: '#10B981' }}>{formatCurrency(selectedGym.totalRevenue)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280' }}>Monthly Price: </span>
                      <strong>{formatCurrency(selectedGym.monthlyPrice)}/mo</strong>
                    </div>
                    {selectedGym.isTrial && (
                      <>
                        <div>
                          <span style={{ color: '#6B7280' }}>Trial: </span>
                          <strong style={{ color: '#F59E0B' }}>{selectedGym.trialDaysRemaining} days left</strong>
                        </div>
                        <div>
                          <span style={{ color: '#6B7280' }}>Trial Ends: </span>
                          <strong>{new Date(selectedGym.createdAt).toLocaleDateString()}</strong>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowManageModal(false);
                    setSelectedGym(null);
                  }}
                  disabled={updating}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: updating ? 'not-allowed' : 'pointer',
                    opacity: updating ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: updating ? 'not-allowed' : 'pointer',
                    opacity: updating ? 0.5 : 1
                  }}
                >
                  {updating ? 'Updating...' : 'Update Gym'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;

